import { useCart } from "@/lib/cart";
import { useCartTotals } from "@/lib/cart-totals";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingCart, Trash2, Zap, Upload, Plus, Minus, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFormatMoney } from "@/lib/currency";
import { useGangSheetBuilderScript } from "@/hooks/useGangSheetBuilderScript";
import { toast } from "sonner";

export function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateItem } = useCart();
  const totals = useCartTotals(items);
  const fmt = useFormatMoney();
  const { openBuilder, status: builderStatus, error: builderError } = useGangSheetBuilderScript();

  const editGangSheet = async (itemId: string) => {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    if (builderStatus === "checking" || builderStatus === "loading") {
      toast.info("Gang sheet builder is still loading — try again in a moment.");
      return;
    }
    if (builderStatus !== "ready") {
      toast.error(builderError ?? "Gang sheet builder isn't available right now.");
      return;
    }
    try {
      await openBuilder({
        product_code: item.productCode,
        design_id: item.gangSheetLayoutId ?? undefined,
        onSaved: (designId, info) => {
          updateItem(itemId, {
            gangSheetLayoutId: designId,
            gangSheetPreviewUrl: info?.previewUrl ?? null,
            gangSheetSavedAt: info?.savedAt ?? new Date().toISOString(),
            uploadedArtworkUrl: null,
          });
          toast.success("Gang sheet updated");
        },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not open the gang sheet builder.");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={closeCart}
          />

          {/* Drawer */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-background shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                <h2 className="text-lg font-semibold">Your cart</h2>
                {items.length > 0 && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                    {items.length}
                  </span>
                )}
              </div>
              <button
                onClick={closeCart}
                className="rounded-full p-2 hover:bg-muted transition-colors"
                aria-label="Close cart"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <ShoppingCart className="h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">Your cart is empty.</p>
                  <Link to="/app/customer/shop" onClick={closeCart}>
                    <Button variant="outline" size="sm" className="mt-4 rounded-full">
                      Browse shop
                    </Button>
                  </Link>
                </div>
              ) : (
                <ul className="space-y-3">
                  {items.map((item) => {
                    const line = totals.lines.find((l) => l.id === item.id);
                    const lineTotal = line?.net ?? 0;
                    return (
                      <li
                        key={item.id}
                        className="rounded-2xl border border-border bg-card p-3 flex gap-3"
                      >
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-muted">
                          {item.productImage ? (
                            <img
                              src={item.productImage}
                              alt={item.productName}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="h-full w-full bg-muted" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold truncate">{item.productName}</p>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-muted-foreground hover:text-destructive shrink-0"
                              aria-label="Remove item"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {item.gangSheetName && (
                            <p
                              className="text-xs text-muted-foreground truncate"
                              title={item.gangSheetName}
                            >
                              <span className="font-medium text-foreground/80">Design:</span>{" "}
                              {item.gangSheetName}
                            </p>
                          )}
                          {item.variantLabel && (
                            <p className="text-xs text-muted-foreground">{item.variantLabel}</p>
                          )}
                          <p className="text-[11px] text-muted-foreground tabular-nums">
                            {fmt(item.pricePerMetre)}/m × {item.metres}m
                            {item.priceModifier ? (
                              <span className="ml-1 text-amber-700 dark:text-amber-300">
                                (incl. {item.priceModifier > 0 ? "+" : ""}
                                {fmt(item.priceModifier)}/m option)
                              </span>
                            ) : null}
                          </p>
                          {item.requiresGangSheet && (
                            <div className="mt-0.5 space-y-1">
                              <p className="text-[11px] text-amber-700 dark:text-amber-300">
                                {item.gangSheetLayoutId
                                  ? "Gang sheet ready"
                                  : item.uploadedArtworkUrl
                                    ? "Artwork uploaded"
                                    : "Gang sheet pending"}
                              </p>
                              {item.gangSheetLayoutId && (
                                <div className="flex items-center gap-2">
                                  {item.gangSheetPreviewUrl && (
                                    <img
                                      src={item.gangSheetPreviewUrl}
                                      alt="Gang sheet preview"
                                      className="h-8 w-8 rounded border border-border object-contain bg-background"
                                      onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.display =
                                          "none";
                                      }}
                                    />
                                  )}
                                  {item.gangSheetSavedAt && (
                                    <span className="text-[10px] text-muted-foreground">
                                      Saved{" "}
                                      {new Date(item.gangSheetSavedAt).toLocaleString(undefined, {
                                        dateStyle: "short",
                                        timeStyle: "short",
                                      })}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => editGangSheet(item.id)}
                                    className="ml-auto inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                                  >
                                    <Pencil className="h-3 w-3" /> Edit
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="mt-2 flex items-center justify-between">
                            <div className="flex items-center gap-1 rounded-full border border-border">
                              <button
                                onClick={() =>
                                  updateItem(item.id, {
                                    quantity: Math.max(1, item.quantity - 1),
                                  })
                                }
                                className="p-1 hover:bg-muted rounded-l-full"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="px-2 text-xs font-medium tabular-nums">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateItem(item.id, { quantity: item.quantity + 1 })}
                                className="p-1 hover:bg-muted rounded-r-full"
                                aria-label="Increase quantity"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                            <span className="text-sm font-semibold tabular-nums">
                              {fmt(lineTotal)}
                            </span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-border p-6 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold tabular-nums">{fmt(totals.subtotal)}</span>
                </div>
                {totals.taxEnabled && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {totals.taxLabel}
                      {totals.taxRateLabel}
                    </span>
                    <span className="font-semibold tabular-nums">{fmt(totals.tax)}</span>
                  </div>
                )}
                {totals.taxEnabled && (
                  <div className="flex items-center justify-between text-sm border-t border-border pt-2">
                    <span className="font-semibold">Total</span>
                    <span className="font-semibold tabular-nums">{fmt(totals.total)}</span>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Shipping and final pricing calculated at checkout.
                </p>
                <Link to="/app/customer/cart" onClick={closeCart}>
                  <Button variant="outline" className="w-full rounded-full">
                    View cart
                  </Button>
                </Link>
                <Link to="/app/customer/checkout" onClick={closeCart}>
                  <Button className="w-full rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90 gap-2">
                    <Zap className="h-4 w-4" />
                    Checkout
                  </Button>
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export function CartButton({ className }: { className?: string }) {
  const { getItemCount, openCart } = useCart();
  const count = getItemCount();

  return (
    <button
      onClick={openCart}
      className={cn(
        "relative inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-muted transition-colors",
        className,
      )}
      aria-label={`Open cart (${count} items)`}
    >
      <ShoppingCart className="h-4 w-4" />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
          {count}
        </span>
      )}
    </button>
  );
}
